import { gql } from "@apollo/client";
import {
    AdditionalChargesTemplateDatalistQuery,
    AdditionalChargesTemplateDatalistQueryVariables,
    BookingAdditionalChargesTemplateFilterInput,
    BookingAdditionalChargesTemplateSortInput,
    SortEnumType
} from "types/graphql";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import useAppContext from "utilities/hooks/useAppContext";
import { graph } from "config/axios-context";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import { uuid } from "config/app-constants";
import MoneyView from "components/common/views/MoneyView";
import { Roles } from "utilities/app-utilities";
import { useSearchParams } from "react-router-dom";
import BookingAdditionalChargesTemplateForm from "./BookingAdditionalChargesTemplateForm";
import ModalPortal from "components/modal/ModalPortal";
import * as lodash from "lodash";
import { useRef } from "react";
import { PartialDeep } from "type-fest";
import { deleteMutation } from "utilities/entities/common/delete-mutation";

export const ADDITIONAL_CHARGES_TEMPLATE_DATALIST_DEFAULT_COLUMNS: DatalistPropsType<DataItem, BookingAdditionalChargesTemplateFilterInput, BookingAdditionalChargesTemplateSortInput>['table']['columns'] = [
    {
        name: 'id',
    },
    {
        name: 'name'
    },
    {
        name: 'calculationType',
        resolve: (i, _, context) => i?.calculationType ? context.t(`accommodation.additionalChargesTemplate.calculationType.${i.calculationType.toLocaleLowerCase()}`) : ''
    },
    {
        name: 'amount',
        type: 'number',
        resolve: i => <MoneyView value={i.amount} />
    },
    {
        name: 'isIncome',
        type: 'boolean',
    }
];

export type AdditionalChargesTemplateDatalistPropsType = {
    config?: PartialDeep<DatalistPropsType<DataItem, BookingAdditionalChargesTemplateFilterInput, BookingAdditionalChargesTemplateSortInput>>,
    fixedFilters?: BookingAdditionalChargesTemplateFilterInput,
    name: string
}

export const BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM = 'booking-additional-charges-template-datalist-form';

type DataItem = NonNullable<AdditionalChargesTemplateDatalistQuery['bookingAdditionalChargesTemplates']>['items'][0];

const query = gql`
query additionalChargesTemplateDatalist($where: BookingAdditionalChargesTemplateFilterInput, $order: [BookingAdditionalChargesTemplateSortInput!], $take: Int, $skip: Int) {
  bookingAdditionalChargesTemplates {
    items(order: $order, skip: $skip, take:$take, where: $where) {
      amount,
      calculationType
      name
      id
      isIncome
    } 
    count(where: $where)
  }
}
`;

const AdditionalChargesTemplateDatalist = (props: AdditionalChargesTemplateDatalistPropsType) => {
    const context = useAppContext();
    const [searchParams] = useSearchParams();
    const datalistRef = useRef<Datalist<DataItem, BookingAdditionalChargesTemplateFilterInput, BookingAdditionalChargesTemplateSortInput>>(null);
    const defaultConfig: DatalistPropsType<DataItem, BookingAdditionalChargesTemplateFilterInput, BookingAdditionalChargesTemplateSortInput> = {
        dataSource: {
            request: async params => {
                const variables: AdditionalChargesTemplateDatalistQueryVariables = {
                    order: params.sort,
                    skip: params.skip,
                    take: params.take,
                    where: params.filters
                }

                if (props.fixedFilters) {
                    variables.where = appendFilters(variables.where, props.fixedFilters);
                }

                if (params.searchText) {
                    const search: BookingAdditionalChargesTemplateFilterInput = {
                        or: [
                            {
                                name: {
                                    contains: params.searchText
                                }
                            }
                        ]
                    }

                    variables.where = appendFilters(variables.where, search)
                }

                try {
                    const response = await graph<AdditionalChargesTemplateDatalistQuery, AdditionalChargesTemplateDatalistQueryVariables>({
                        query,
                        variables
                    });

                    if (response.errors?.length) {
                        throw response.errors;
                    }

                    return response.data!.bookingAdditionalChargesTemplates!;
                } catch (e: any) {
                    context.notify('error', 'request.load.error', Array.isArray(e) ? e[0].message : e.message);
                    throw e;
                }
            },
            sort: [
                {
                    name: 'name',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ]
        },
        defaultView: 'only-table',
        itemId: i => i.id,
        name: `${props.name}-additional-charges-template-datalist`,
        table: {
            columns: [...ADDITIONAL_CHARGES_TEMPLATE_DATALIST_DEFAULT_COLUMNS],
            render: {
                actions: {
                    mutationPermissionRole: context.userHasRole(Roles.ADMIN),
                    editUrl: {
                        type: 'modal',
                        url: BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM
                    },
                    delete: async (item: DataItem, e: React.MouseEvent) => {
                        await deleteMutation('bookingAdditionalChargesTemplateDelete', item.id);
                    }
                }
            }
        }
    }

    const config = lodash.merge(defaultConfig, props.config);

    return (
        <>
            <Datalist ref={datalistRef} {...config} />
            <ModalPortal name={BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM} title="pages.accommodation.additionalChargesTemplate.form">
                {
                    searchParams.get(BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM) ?
                        <BookingAdditionalChargesTemplateForm
                            name="add-data-list"
                            model={context.modal.getData(BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM)}
                            onSave={() => {
                                context.modal.close(BOOKING_ADDITIONAL_CHARGES_TEMPLATE_DATALIST_FORM);
                                datalistRef.current?.reload();
                            }}
                        />
                        : <></>
                }
            </ModalPortal>
        </>
    );
};

export default AdditionalChargesTemplateDatalist;
